import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AutoComplete, Button, Input, Select, Upload } from 'antd';
import {
  DEFAULT_VERTEX_REGION,
  MAX_SERVICE_ACCOUNT_BYTES,
  MODEL_PROVIDERS,
  VERTEX_REGIONS,
  getModelKey,
  readServiceAccount,
  removeModelKey,
  saveModelKey,
} from '../../services/ModelKeyService';
import { updateSettings } from '../../services/SettingsService';
import { setChatEnabled } from '../../hooks/useChatFeature';
import { refreshPlanFeatures } from '../../hooks/usePlanFeatures';
import { formatRelativeTime } from '../../utils/relativeTime';
import VendorName from './VendorName';
import './ModelKeyForm.css';

/**
 * "Use my own key" for Flow's assistant (sandbox-first, docs/SANDBOX_FIRST.md), shared by Features › Chat, the
 * first-run setup and Settings: provider, key and (for an OpenAI-compatible provider) its URL, then "Check and save"
 * through mia-flow-bff, which tries the key once. Google Vertex AI takes a service-account .json key file instead
 * (chosen, read here with FileReader, or pasted), its project ID (from the file, editable) and a region. Once saved
 * it shows only what the BFF returns (the last four characters, or the service account, project and region, and when
 * it was checked), with Replace and Remove. The key is never logged nor kept in the browser: it leaves the form's
 * state as soon as it is saved.
 *
 * Saving also makes the own key the person's choice (`onboarding.keySource: 'own'`) and turns Chat on.
 * `onChange(info)` reports what is saved (`{ configured, ... }`), or `null` when this Flow cannot keep a key.
 */
export default function ModelKeyForm({ onChange, idPrefix = 'model-key' }) {
  const [info, setInfo] = useState(undefined);
  const [editing, setEditing] = useState(false);
  const [provider, setProvider] = useState(MODEL_PROVIDERS[0].value);
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [keyFile, setKeyFile] = useState({ text: '', name: '' });
  const [project, setProject] = useState('');
  const [region, setRegion] = useState(DEFAULT_VERTEX_REGION);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const update = (next) => {
    setInfo(next);
    onChange?.(next);
  };

  useEffect(() => {
    let cancelled = false;
    getModelKey()
      .then((next) => { if (!cancelled) { setInfo(next); onChange?.(next); } })
      .catch(() => { if (!cancelled) { setInfo({ configured: false }); onChange?.({ configured: false }); } });
    return () => { cancelled = true; };
    // Loaded once; `onChange` is a notification, not an input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needsUrl = provider === 'openai-compatible';
  const vertex = provider === 'vertex';
  const account = vertex ? readServiceAccount(keyFile.text) : {};
  const canSave = !busy && (vertex
    ? Boolean(account.clientEmail) && project.trim() !== '' && region.trim() !== ''
    : apiKey.trim() !== '' && (!needsUrl || baseUrl.trim() !== ''));

  /** A pasted or chosen key file: kept in state until saved, its project ID prefilled. */
  const takeKeyFile = (text, name = '') => {
    setKeyFile({ text, name });
    const read = readServiceAccount(text);
    if (read.projectId) { setProject(read.projectId); }
  };

  const chooseFile = (file) => {
    if (file.size > MAX_SERVICE_ACCOUNT_BYTES) {
      setKeyFile({ text: '', name: '' });
      setError('This file is too large to be a service account key.');
      return false;
    }
    const reader = new FileReader();
    reader.onload = () => { setError(null); takeKeyFile(String(reader.result || ''), file.name); };
    reader.onerror = () => setError('The file could not be read.');
    reader.readAsText(file);
    // Read here, never uploaded by antd: the key goes to the BFF only with "Check and save".
    return false;
  };

  const save = async() => {
    setBusy(true);
    setError(null);
    try {
      const next = await saveModelKey(vertex
        ? { provider, apiKey: keyFile.text.trim(), project: project.trim(), region: region.trim() }
        : { provider, apiKey: apiKey.trim(), baseUrl: baseUrl.trim() });
      setApiKey('');
      setKeyFile({ text: '', name: '' });
      setEditing(false);
      update({ configured: true, provider, ...next });
      setChatEnabled(true);
      await updateSettings({ onboarding: { keySource: 'own' } }).catch(() => {});
      // The chat reads the choice (and whether AI Foundry is in use) from there.
      refreshPlanFeatures();
    } catch (err) {
      setError(err.status === 400 ? err.message : `Your key could not be checked: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async() => {
    setBusy(true);
    setError(null);
    try {
      update(await removeModelKey());
    } catch (err) {
      setError(`Your key could not be removed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (info === undefined) { return <span className="model-key-note">Checking your key…</span>; }
  if (info === null) {
    return <span className="model-key-note">Using your own key needs a newer Flow backend. Ask your admin.</span>;
  }

  if (info.configured && !editing) {
    const providerName = MODEL_PROVIDERS.find((item) => item.value === info.provider)?.label;
    const what = info.provider === 'vertex'
      ? [info.clientEmail ? `Service account ${info.clientEmail}` : null, info.project, info.region]
      : [info.last4 ? `****${info.last4}` : null];
    return (
      <div className="model-key">
        <span className="model-key-saved" role="status">
          <span aria-hidden="true" className="sbx-dot" />
          {'Saved'}
          {providerName && <>{' · '}<VendorName name={providerName} size={12} /></>}
          {[...what, info.checkedAt ? `checked ${formatRelativeTime(info.checkedAt)}` : null].filter(Boolean).map((part) => ` · ${part}`).join('')}
        </span>
        <div className="model-key-actions">
          <Button
            disabled={busy}
            onClick={() => {
              setEditing(true);
              setProvider(info.provider || MODEL_PROVIDERS[0].value);
              setBaseUrl(info.baseUrl || '');
              setProject(info.project || '');
              setRegion(info.region || DEFAULT_VERTEX_REGION);
            }}
          >
            Replace
          </Button>
          <Button danger loading={busy} onClick={remove}>Remove</Button>
        </div>
        {error && <span className="sbx-action-error" role="alert">{error}</span>}
      </div>
    );
  }

  return (
    <div className="model-key">
      <div className="model-key-fields">
        <span className="model-key-field">
          <label htmlFor={`${idPrefix}-provider`}>Provider</label>
          <Select
            id={`${idPrefix}-provider`}
            labelRender={({ label }) => <VendorName name={String(label || '')}>{label}</VendorName>}
            onChange={setProvider}
            optionRender={({ label }) => <VendorName name={String(label || '')}>{label}</VendorName>}
            options={MODEL_PROVIDERS}
            value={provider}
          />
        </span>
        {!vertex && (
          <span className="model-key-field">
            <label htmlFor={`${idPrefix}-key`}>API key</label>
            <Input.Password
              autoComplete="off"
              id={`${idPrefix}-key`}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder="Paste your key"
              value={apiKey}
            />
          </span>
        )}
        {vertex && (
          <>
            <span className="model-key-field model-key-wide">
              <label htmlFor={`${idPrefix}-sa`}>Service account key (.json)</label>
              {keyFile.text && account.clientEmail ? (
                <span className="model-key-file">
                  <span>{[keyFile.name || 'Pasted key', account.clientEmail].join(' · ')}</span>
                  <Button onClick={() => setKeyFile({ text: '', name: '' })} size="small" type="link">Clear</Button>
                </span>
              ) : (
                <>
                  <Upload accept=".json,application/json" beforeUpload={chooseFile} maxCount={1} showUploadList={false}>
                    <Button>Choose .json file</Button>
                  </Upload>
                  <Input.TextArea
                    autoComplete="off"
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    id={`${idPrefix}-sa`}
                    onChange={(event) => takeKeyFile(event.target.value)}
                    placeholder="…or paste the JSON key here"
                    spellCheck={false}
                    value={keyFile.text}
                  />
                  {account.error && <span className="sbx-action-error" role="alert">{account.error}</span>}
                </>
              )}
            </span>
            <span className="model-key-field">
              <label htmlFor={`${idPrefix}-project`}>Project ID</label>
              <Input id={`${idPrefix}-project`} onChange={(event) => setProject(event.target.value)} placeholder="my-gcp-project" value={project} />
            </span>
            <span className="model-key-field">
              <label htmlFor={`${idPrefix}-region`}>Region</label>
              <AutoComplete
                filterOption={(input, option) => option.value.includes(input.trim().toLowerCase())}
                id={`${idPrefix}-region`}
                onChange={setRegion}
                options={VERTEX_REGIONS.map((value) => ({ value }))}
                placeholder={DEFAULT_VERTEX_REGION}
                value={region}
              />
            </span>
          </>
        )}
        {needsUrl && (
          <span className="model-key-field">
            <label htmlFor={`${idPrefix}-url`}>Base URL</label>
            <Input id={`${idPrefix}-url`} onChange={(event) => setBaseUrl(event.target.value)} placeholder="https://llm.example.com/v1" value={baseUrl} />
          </span>
        )}
      </div>
      <div className="model-key-actions">
        <Button disabled={!canSave} loading={busy} onClick={save} type="primary">Check and save</Button>
        {editing && <Button disabled={busy} onClick={() => { setEditing(false); setApiKey(''); setKeyFile({ text: '', name: '' }); setError(null); }}>Cancel</Button>}
        <span className="model-key-note">
          {vertex
            ? 'Flow signs in once to check that the service account can use Vertex AI (it needs the Vertex AI User role). The key is stored encrypted and used only for your chats.'
            : 'Flow tries it once with a small request. It is stored encrypted, used only for your chats and never shown again.'}
        </span>
      </div>
      {error && <span className="sbx-action-error" role="alert">{error}</span>}
    </div>
  );
}

ModelKeyForm.propTypes = {
  onChange: PropTypes.func,
  idPrefix: PropTypes.string,
};
